// Wishlist for logged-in customers. Stored in the customer metafield wishlist.items, rendered in Liquid
// (templates/customers/account.wishlist.liquid) and changed through the backend
// (bloominspo-services, /api/tarsio/wishlist).
// Config (API URL, customer token, saved products, strings) comes from snippets/wishlist-config.liquid.
// Guests are asked to log in; the product they wanted to save is added after login.

const wishlistConfig = window.tarsioWishlist || {};
const wishlistStrings = wishlistConfig.strings || {};

// Old wishlist kept in the browser before the backend existed. Imported after login.
const LEGACY_STORAGE_KEY = "tarsiowishlist";
// Product a guest wanted to save before being sent to log in.
const PENDING_STORAGE_KEY = "tarsiowishlist:pending";

function readStorage(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    // Ignore (e.g. storage disabled)
  }
}

function readLegacyCookie() {
  try {
    const cookie = document.cookie.split(";").find((c) => c.trim().startsWith(`${LEGACY_STORAGE_KEY}=`));
    if (!cookie) return [];
    const parsed = JSON.parse(decodeURIComponent(cookie.trim().substring(LEGACY_STORAGE_KEY.length + 1)));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function clearLegacyCookie() {
  document.cookie = `${LEGACY_STORAGE_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// Product ids from the raw metafield value rendered by Liquid (["gid://shopify/Product/123", ...]).
// Returns null if the value can't be read, so the store loads the wishlist from the backend instead.
function parseSavedProductIds(raw) {
  if (typeof raw !== "string") return null;
  if (raw === "") return [];
  const ids = [...raw.matchAll(/Product\/(\d+)/g)].map((match) => Number(match[1]));
  return ids.length > 0 ? ids : null;
}

class WishlistError extends Error {}

class WishlistStore {
  constructor() {
    this.savedProductIds = parseSavedProductIds(wishlistConfig.savedRaw);
    this.productIds = this.savedProductIds || [];
    this.loaded = null;
  }

  get enabled() {
    return Boolean(wishlistConfig.token && wishlistConfig.apiBase);
  }

  has(productId) {
    return this.productIds.includes(Number(productId));
  }

  load() {
    if (!this.loaded) {
      this.loaded = this.init().catch((error) => {
        // Allow retrying on the next call
        this.loaded = null;
        throw error;
      });
    }
    return this.loaded;
  }

  async init() {
    // Legacy entries are product JSON from the old snippet ({ id, ... }), pending ones are { id }.
    const localProductIds = [...readStorage(LEGACY_STORAGE_KEY), ...readLegacyCookie(), ...readStorage(PENDING_STORAGE_KEY)]
      .map((product) => Number(product?.id))
      .filter(Boolean);

    if (localProductIds.length === 0) {
      return this.savedProductIds ? this.productIds : this.request("");
    }

    const productIds = await this.request("/import", { method: "POST", body: { productIds: localProductIds } });
    removeStorage(LEGACY_STORAGE_KEY);
    removeStorage(PENDING_STORAGE_KEY);
    clearLegacyCookie();
    return productIds;
  }

  add(productId) {
    return this.request("/items", { method: "POST", body: { productId } });
  }

  remove(productId) {
    return this.request(`/items/${encodeURIComponent(productId)}`, { method: "DELETE" });
  }

  async request(path, { method = "GET", body } = {}) {
    const headers = { Authorization: `Bearer ${wishlistConfig.token}` };
    if (body) headers["Content-Type"] = "application/json";

    const response = await fetch(`${wishlistConfig.apiBase}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (response.status === 401) {
      // Token expired (page open for too long) - a reload renders a new one.
      throw new WishlistError(wishlistStrings.sessionExpired);
    }

    const data = await response.json().catch(() => ({}));
    if (data.statusCode === "WISHLIST_LIMIT_REACHED") {
      throw new WishlistError(wishlistStrings.limitReached);
    }
    if (!response.ok || !data.success) {
      throw new WishlistError(wishlistStrings.error);
    }

    this.productIds = data.productIds;
    document.dispatchEvent(new CustomEvent("wishlist:change", { detail: { productIds: this.productIds } }));
    return this.productIds;
  }
}

const wishlistStore = new WishlistStore();

// --- Login prompt for guests ---

function savePendingProduct(productId) {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify([{ id: productId }]));
  } catch (error) {
    // Ignore - the customer can add the product again after login
  }
}

/**
 * Opens the login prompt. After login the customer is sent back to `returnUrl`
 * and `productId` (if given) is added to their wishlist.
 */
function openWishlistLogin({ returnUrl = location.pathname + location.search, productId = null } = {}) {
  const modal = document.getElementById("wishlist-login-modal");
  if (!modal) return;

  const loginLink = modal.querySelector("[data-wishlist-login-link]");
  if (loginLink) {
    const url = new URL(loginLink.dataset.wishlistLoginLink, location.origin);
    url.searchParams.set("return_url", returnUrl);
    loginLink.href = url.pathname + url.search;
  }

  modal.querySelectorAll("[data-wishlist-login-link], [data-wishlist-register-link]").forEach((link) => {
    link.onclick = () => productId && savePendingProduct(productId);
  });

  modal.show();
}

document.addEventListener("click", (event) => {
  const trigger = event.target.closest("[data-wishlist-login-prompt]");
  if (!trigger) return;
  event.preventDefault();
  openWishlistLogin({ returnUrl: trigger.dataset.returnUrl || wishlistConfig.wishlistUrl });
});

// --- Heart button (product card, product page) ---

class AddToWishlist extends HTMLElement {
  connectedCallback() {
    this.addButton = this.querySelector(".add-to-wishlist-button");
    this.productId = Number(this.dataset.productId);
    if (!this.productId) return;

    this.onClick = this.handleClick.bind(this);
    this.onChange = this.render.bind(this);
    this.addButton?.addEventListener("click", this.onClick);
    document.addEventListener("wishlist:change", this.onChange);

    this.render();
    if (wishlistStore.enabled) {
      wishlistStore.load().catch((error) => console.error("Wishlist:", error.message));
    }
  }

  disconnectedCallback() {
    this.addButton?.removeEventListener("click", this.onClick);
    document.removeEventListener("wishlist:change", this.onChange);
  }

  render() {
    this.setFilled(wishlistStore.has(this.productId));
  }

  setFilled(filled) {
    this.addButton?.classList.toggle("fill", filled);
  }

  async handleClick(event) {
    // The heart can sit inside a product link
    event.preventDefault();
    event.stopPropagation();

    if (!wishlistConfig.loggedIn) {
      openWishlistLogin({ productId: this.productId });
      return;
    }

    if (this.busy) return;
    this.busy = true;

    try {
      if (!wishlistStore.enabled) throw new WishlistError(wishlistStrings.error);

      await wishlistStore.load();
      const remove = wishlistStore.has(this.productId);
      // Optimistic update, the store event re-renders with the real state
      this.setFilled(!remove);
      if (remove) {
        await wishlistStore.remove(this.productId);
      } else {
        await wishlistStore.add(this.productId);
      }
    } catch (error) {
      this.render();
      alert(error.message || wishlistStrings.error);
    } finally {
      this.busy = false;
    }
  }
}
customElements.define("add-to-wishlist", AddToWishlist);

class WishlistNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById("wishlist-notification");
    this.header = document.querySelector("sticky-header");
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (!this.notification) {
      console.error("Wishlist: Notification element not found");
      return;
    }

    this.notification.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) => closeButton.addEventListener("click", this.close.bind(this)));
  }

  open() {
    if (!this.notification) return;

    this.notification.classList.add("animate", "active");

    this.notification.addEventListener(
      "transitionend",
      () => {
        if (this.notification) {
          this.notification.focus();
          if (typeof trapFocus === "function") {
            trapFocus(this.notification);
          }
        }
      },
      { once: true }
    );

    document.body.addEventListener("click", this.onBodyClick);
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove("active");
    document.body.removeEventListener("click", this.onBodyClick);

    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(this.activeElement);
    }
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: "wishlist-notification-product",
        selector: `[id="wishlist-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: "wishlist-notification-button",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest("wishlist-notification")) {
      const disclosure = target.closest("details-disclosure, header-menu");
      this.activeElement = disclosure ? disclosure.querySelector("summary") : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("wishlist-notification", WishlistNotification);
